/**
 * Turn the browse-page URL search params into the param map the
 * listings API expects (GET /api/listings + /api/listings/map).
 *
 * Single source of truth so the list grid and the map view always
 * forward the SAME filters to their respective endpoints - they can
 * never drift on which params count as multi-value or which get sent.
 */
import type { ReadonlyURLSearchParams } from "next/navigation";
import { decodeRings, ringsToPolygonParams } from "@/lib/draw-area";

// Params that may repeat in the URL and must be forwarded as arrays.
// `city`: towns outside Baku by name (the search bar's city rows).
// `exclude_location_ids`, `exclude_city`: places ruled out ("everywhere
// but Masazır and Xırdalan", the search bar).
const MULTI_KEYS = new Set(["rooms", "location_ids", "city", "deal_tier", "listing_kind", "complex_slug", "developer", "photo_condition", "photo_has", "tags", "exclude_tags", "mortgage_years", "source", "property_kind", "fact", "exclude_location_ids", "exclude_city"]);

// Scalar params that are forwarded verbatim to the API without any
// transformation. Listing-page UI does not manage these via its own
// filter controls - they arrive from deep-links (e.g. /invest per-
// district rows) and persist across filter changes as transparent pass-
// through values until the user navigates away.
const PASSTHROUGH_SCALAR_KEYS = new Set(["district"]);

/**
 * The URL's room values as the API takes them. The "5+" rooms chip sends
 * rooms=5, and the API matches room counts exactly, so "5+" used to mean
 * five rooms only. Widen it to every count the index holds (the SEO
 * pages' 5+ bucket uses the same range).
 */
export function widenRooms(rooms: string[]): string[] {
  if (!rooms.includes("5")) return rooms;
  return [...rooms.filter((r) => Number(r) < 5), ...Array.from({ length: 16 }, (_, i) => String(5 + i))];
}

/**
 * Read every browse filter param off the URL into an API param map.
 * Pagination (``page`` / ``page_size``) is intentionally NOT added -
 * the caller owns that (the grid paginates; the map fetches the full
 * set). ``sort`` is preserved if present.
 */
export function apiParamsFromSearch(
  searchParams: ReadonlyURLSearchParams,
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};
  const seen = new Set<string>();
  for (const [k, v] of Array.from(searchParams.entries())) {
    // `open` deep-links a listing drawer on the browse page; the API
    // never sees it.
    if (k === "page" || k === "page_size" || k === "open" || k === "from") continue;
    // `draw` holds the map's drawn areas (lib/draw-area), expanded here
    // into the API's repeatable `polygon` rings so the list, the map and
    // the count all filter by them (feedback ticket 16, 2026-10-15).
    if (k === "draw") {
      if (seen.has(k)) continue;
      seen.add(k);
      const polygon = ringsToPolygonParams(decodeRings(v));
      if (polygon.length) params.polygon = polygon;
      continue;
    }
    if (MULTI_KEYS.has(k)) {
      if (!seen.has(k)) {
        params[k] = searchParams.getAll(k);
        seen.add(k);
      }
    } else {
      params[k] = v;
    }
  }
  const rooms = params.rooms;
  if (Array.isArray(rooms)) params.rooms = widenRooms(rooms);
  // Transaction default is PURCHASE: an absent `leased` means sale-only;
  // the explicit `leased=all` sentinel (URL-only, the API doesn't know it)
  // widens back to everything.
  if (params.leased === "all") delete params.leased;
  else if (params.leased === undefined) params.leased = "false";
  return params;
}


/**
 * Stable key for an API param map (sort included, defaulting to
 * "newest"): the server stamps the query it pre-fetched with it, and the
 * grid reuses that first page only while its own query has the same key.
 */
export function seedKeyFor(params: Record<string, string | string[]>): string {
  const withSort: Record<string, string | string[]> = { sort: "newest", ...params };
  return JSON.stringify(
    Object.keys(withSort).sort().map((k) => {
      const v = withSort[k];
      return [k, Array.isArray(v) ? [...v].sort() : v];
    }),
  );
}
